import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDown from "@components/ui/dropdown/DropDown";
import TextField from "@components/ui/textfields/TextField";
import DatePicker from "@components/ui/selection/DatePicker";
import TimePicker from "@components/ui/selection/TimePicker";
import { useState } from "react";
import type { VehicleReservation, Vehicle } from "types/garage/vehicules";
import { hourToTimeStr, timeStrToHour } from "@utils/timeGrid";

/** Convert ISO date string (YYYY-MM-DD) to unix seconds */
function isoToSeconds(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(iso + "T12:00:00");
  return Math.floor(d.getTime() / 1000);
}

/** Convert unix seconds to ISO date string (YYYY-MM-DD) */
function secondsToIso(seconds: number): string {
  const d = new Date(seconds * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

interface ReservationModalProps {
  reservation: VehicleReservation;
  vehicles: Vehicle[];
  onClose: () => void;
  onSave: (updated: Partial<VehicleReservation>) => void;
}

export default function ReservationModal({
  reservation,
  vehicles,
  onClose,
  onSave,
}: ReservationModalProps) {
  const [form, setForm] = useState({ ...reservation });

  const isMultiDay = form.startDate !== form.endDate;

  const update = <K extends keyof VehicleReservation>(key: K, value: VehicleReservation[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSave({
      clientName: form.clientName,
      vehicleId: form.vehicleId,
      startDate: form.startDate,
      endDate: form.endDate,
      startHour: form.startHour,
      durationHours: form.durationHours,
      returnHour: form.returnHour,
    });
    onClose();
  };

  const vehicleOptions = vehicles.map((v) => ({
    value: v.id,
    label: `${v.name} — ${v.plate}`,
  }));

  // Compute end time string for single-day reservations
  const endTimeStr = !isMultiDay && form.startHour != null && form.durationHours != null
    ? hourToTimeStr(form.startHour + form.durationHours)
    : "";

  return (
    <BaseModal
      title="Modifier la réservation"
      startIcon={<Icon1 name="modify" size={20} />}
      onClose={onClose}
      width="max-w-3xl"
    >
      <div className="flex flex-col gap-3 p-4">
        {/* Client */}
        <TextField
          label="Client"
          value={form.clientName}
          onChange={(v) => update("clientName", v)}
        />

        {/* Vehicle */}
        <DropDown
          label="Véhicule"
          options={vehicleOptions}
          value={form.vehicleId}
          onSelect={(v) => update("vehicleId", v as string)}
        />

        {/* Dates */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <DatePicker
              label="Date début"
              seconds={isoToSeconds(form.startDate)}
              onChange={(s) => update("startDate", secondsToIso(s))}
            />
          </div>
          <div className="flex-1">
            <DatePicker
              label="Date fin"
              seconds={isoToSeconds(form.endDate)}
              onChange={(s) => update("endDate", secondsToIso(s))}
              minDateSeconds={isoToSeconds(form.startDate)}
            />
          </div>
        </div>

        {/* Hours */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TimePicker
              label="Heure de départ"
              value={form.startHour != null ? hourToTimeStr(form.startHour) : null}
              onChange={(timeStr) => {
                if (!timeStr) return;
                const newStart = timeStrToHour(timeStr);
                // For single-day, keep end hour consistent by adjusting duration
                if (!isMultiDay && form.startHour != null && form.durationHours != null) {
                  const endHour = form.startHour + form.durationHours;
                  update("durationHours", Math.max(0.25, endHour - newStart));
                }
                update("startHour", newStart);
              }}
            />
          </div>
          <div className="flex-1">
            <TimePicker
              label="Heure de fin"
              value={
                isMultiDay
                  ? (form.returnHour != null ? hourToTimeStr(form.returnHour) : null)
                  : (endTimeStr || null)
              }
              onChange={(timeStr) => {
                if (!timeStr) return;
                const endHour = timeStrToHour(timeStr);
                if (isMultiDay) {
                  update("returnHour", endHour);
                } else {
                  const start = form.startHour ?? 8;
                  update("durationHours", Math.max(0.25, endHour - start));
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Fermer
        </SecondaryButton>
        <Button
          variant="small"
          label="Enregistrer"
          onClick={handleSave}
        />
      </div>
    </BaseModal>
  );
}
